import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Flex,
  Input,
  useToast,
  useBreakpointValue,
  Badge,
  Stack,
  FormControl,
  InputGroup,
  InputLeftElement,
  Select as ChakraSelect,
} from "@chakra-ui/react";
import { MdSearch, MdOutlineFilterAltOff } from "react-icons/md";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import Card from "components/card/Card";
import { useTranslation } from "react-i18next";
import fetchWithToken from "views/auth/signIn/axiosInstance";

const LogsTable = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // API data
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  // Sorting
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortField, setSortField] = useState("timestamp");

  // 
  const [search, setSearch] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ipFilter, setIpFilter] = useState("");
  const [featureTagFilter, setFeatureTagFilter] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const pageId = 23;
  // ---- Fetch logs from /heatmap ----
  const fetchLogs = useCallback(
  async (pageNum = 1, customPerPage = perPage) => {
    try {
      setLoading(true);

      const body = {
        per_page: customPerPage,
        page: pageNum,
        sort_by: sortField,
        sort_dir: sortOrder,
        from: filterFrom,
        to: filterTo,
        ip: ipFilter,               // 🔹 sent to API
        feature_tag: featureTagFilter, // 🔹 sent to API
      };
      const url = `${process.env.REACT_APP_API_URL_Report}heatmap?page_id=${pageId}`;
      const response = await fetchWithToken(url, "POST", body);

      const pageData = response.data || {};
      const logsArray = Array.isArray(pageData.data) ? pageData.data : [];

      setLogs(logsArray);
      setCurrentPage(pageData.current_page || 1);
      setLastPage(pageData.last_page || 1);
      setPerPage(pageData.per_page || customPerPage);
      setTotal(pageData.total || 0);
      setMeta(response.meta || null);
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast({
        title: "Error fetching logs",
        description: error.message,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  },
  [
    toast,
    perPage,
    sortField,
    sortOrder,
    filterFrom,
    filterTo,
    ipFilter,          // ✅ add
    featureTagFilter,  // ✅ add
  ]
);

  useEffect(() => {
    fetchLogs(1, perPage);
  }, [fetchLogs]);

  // ---- Client-side filtering ----
const filteredLogs = logs.filter((log) => {
  let ok = true;

  if (methodFilter) {
    ok = ok && log.method === methodFilter;
  }

  if (statusFilter) {
    ok = ok && String(log.status_code) === statusFilter;
  }

  if (ipFilter.trim()) {
    ok = ok && log.ip && log.ip.includes(ipFilter.trim());
  }

  if (featureTagFilter.trim()) {
    ok = ok && log.feature_tag && log.feature_tag.includes(featureTagFilter.trim());
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    ok =
      ok &&
      (
        log.path +
        " " +
        (log.feature_tag || "") +
        " " +
        (log.ip || "")
      )
        .toLowerCase()
        .includes(q);
  }

  return ok;
});

  // ---- Sorting handler ----
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
    fetchLogs(currentPage, perPage);
  };

  // ---- Filter bar ----
const renderFilterForm = () => (
  <Stack
    direction={{ base: "column", md: "row" }}
    spacing={4}
    w="full"
    align="center"
    justify="flex-end"
  >
    {/* Method filter */}
    <FormControl maxW={{ base: "100%", md: "140px" }}>
      <ChakraSelect
        placeholder={t("Method")}
        value={methodFilter}
        onChange={(e) => setMethodFilter(e.target.value)}
        size="sm"
        focusBorderColor="blue.400"
        borderRadius="md"
        _hover={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182CE",
          backgroundColor: "blue.50",
        }}
      >
        <option value="GET">GET</option>
        <option value="POST">POST</option>
        <option value="PUT">PUT</option>
        <option value="DELETE">DELETE</option>
      </ChakraSelect>
    </FormControl>

    {/* Status code filter */}
    <FormControl maxW={{ base: "100%", md: "160px" }}>
      <ChakraSelect
        placeholder={t("Status code")}
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        size="sm"
        focusBorderColor="blue.400"
        borderRadius="md"
        _hover={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182CE",
          backgroundColor: "blue.50",
        }}
      >
        <option value="200">200</option>
        <option value="400">400</option>
        <option value="401">401</option>
        <option value="403">403</option>
        <option value="404">404</option>
        <option value="500">500</option>
      </ChakraSelect>
    </FormControl>

    {/* IP filter */}
    <FormControl maxW={{ base: "100%", md: "180px" }}>
      <Input
        type="text"
        placeholder={t("Filter by IP address")}
        value={ipFilter}
        onChange={(e) => setIpFilter(e.target.value)}
        size="sm"
        focusBorderColor="blue.400"
        _hover={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182CE",
          color: "blue.400",
          backgroundColor: "blue.50",
        }}
      />
    </FormControl>

    {/* Feature Tag filter */}
    <FormControl maxW={{ base: "100%", md: "180px" }}>
      <Input
        type="text"
        placeholder={t("Filter by Feature Tag")}
        value={featureTagFilter}
        onChange={(e) => setFeatureTagFilter(e.target.value)}
        size="sm"
        focusBorderColor="blue.400"
        _hover={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182CE",
          color: "blue.400",
          backgroundColor: "blue.50",
        }}
      />
    </FormControl>

    {/* From Date */}
    <FormControl maxW={{ base: "100%", md: "180px" }}>
      <Input
        type="datetime-local"
        placeholder={t("From")}
        value={filterFrom}
        onChange={(e) => setFilterFrom(e.target.value)}
        size="sm"
        focusBorderColor="blue.400"
        _hover={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182CE",
          color: "blue.400",
          backgroundColor: "blue.50",
        }}
      />
    </FormControl>

    {/* To Date */}
    <FormControl maxW={{ base: "100%", md: "180px" }}>
      <Input
        type="datetime-local"
        placeholder={t("To")}
        value={filterTo}
        onChange={(e) => setFilterTo(e.target.value)}
        size="sm"
        focusBorderColor="blue.400"
        _hover={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182CE",
          color: "blue.400",
          backgroundColor: "blue.50",
        }}
      />
    </FormControl>

    {/* Per Page */}
    <FormControl maxW={{ base: "100%", md: "120px" }}>
      <ChakraSelect
        value={perPage}
        onChange={(e) => {
          setPerPage(parseInt(e.target.value, 10));
          setCurrentPage(1);
          fetchLogs(1, parseInt(e.target.value, 10));
        }}
        size="sm"
        focusBorderColor="blue.400"
        borderRadius={"md"}
        _hover={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px #3182CE",
          color: "blue.400",
          backgroundColor: "blue.50",
        }}
      >
        <option value="10">10 {t("per page")}</option>
        <option value="20">20 {t("per page")}</option>
        <option value="50">50 {t("per page")}</option>
      </ChakraSelect>
    </FormControl>

    {/* Reset filters */}
    <Button
      leftIcon={<MdOutlineFilterAltOff />}
      variant="outline"
      colorScheme="gray"
      size="sm"
      onClick={() => {
        setSearch("");
        setMethodFilter("");
        setStatusFilter("");
        setIpFilter("");
        setFeatureTagFilter("");
        setFilterFrom("");
        setFilterTo("");
        setCurrentPage(1);
        fetchLogs(1, perPage);
      }}
      _hover={{
        borderColor: "red.500",
        color: "red.500",
        backgroundColor: "rgba(245, 101, 101, 0.1)",
        boxShadow: "0 0 0 2px #F56565",
      }}
    >
      {t("Reset")}
    </Button>
  </Stack>
);

  // ---- Desktop table ----
  const renderDesktopTable = () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      return (
        <Box p="20px" textAlign="center">
          <Text fontSize="md" color="gray.500">
            {loading ? t("Loading logs...") : t("No logs found for this page.")}
          </Text>
        </Box>
      );
    }

    // Smart pagination window
    const pageWindowSize = 5;
    let startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    let endPage = Math.min(lastPage, startPage + pageWindowSize - 1);
    if (endPage - startPage < pageWindowSize - 1) {
      startPage = Math.max(1, endPage - pageWindowSize + 1);
    }
    const pages = [];
    for (let p = startPage; p <= endPage; p++) {
      pages.push(p);
    }

    return (
      <Box px="20px">
        <Box overflowY="auto" maxHeight="400px" px="2px">
          <Table variant="simple" colorScheme="gray">
            <Thead position="sticky" top={0} zIndex={2}>
              <Tr bg="rgb(69, 50, 240)" color="white">
                <Th color="white">{t("Index")}</Th>
                <Th
                  color="white"
                  cursor="pointer"
                  onClick={() => handleSort("timestamp")}
                  userSelect="none"
                >
                  {t("Timestamp")}
                  {sortField === "timestamp" &&
                    (sortOrder === "asc" ? (
                      <TriangleUpIcon ml={2} />
                    ) : (
                      <TriangleDownIcon ml={2} />
                    ))}
                </Th>
                <Th color="white">{t("Method")}</Th>
                <Th color="white">{t("Path")}</Th>
                <Th color="white">{t("IP")}</Th>
                <Th color="white">{t("Feature")}</Th>
                <Th color="white">{t("Status")}</Th>
                <Th color="white">{t("Response time (ms)")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredLogs.map((log, index) => {
                const absoluteIndex = (currentPage - 1) * perPage + index + 1;
                const statusColor =
                  log.status_code >= 500
                    ? "red"
                    : log.status_code >= 400
                    ? "orange"
                    : "green";

                return (
                  <Tr key={log.id}>
                    <Td>{absoluteIndex}</Td>
                    <Td>{log.timestamp}</Td>
                    <Td>
                      <Badge colorScheme="purple" borderRadius="md" px={2}>
                        {log.method}
                      </Badge>
                    </Td>
                    <Td maxW="280px">
                      <Text isTruncated title={log.path}>
                        {log.path}
                      </Text>
                    </Td>
                    <Td>{log.ip}</Td>
                    <Td maxW="220px">
                      <Text isTruncated title={log.feature_tag}>
                        {log.feature_tag}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={statusColor} borderRadius="md" px={2}>
                        {log.status_code}
                      </Badge>
                    </Td>
                    <Td>{Number(log.response_time).toFixed(2)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        {/* Pagination */}
{lastPage > 1 && (
  <Box mt={4} mb={4}>
    <Flex justifyContent="center" mb={3}>
      <Text fontSize="sm" color="gray.600" textAlign="center">
        {t("Page")} {currentPage} {t("of")} {lastPage}
        {filteredLogs.length > 0 && (
          <Text fontSize="xs" color="gray.500">
            {t("Showing")} {(currentPage - 1) * perPage + 1} {t("to")} {Math.min(currentPage * perPage, total)} {t("results")} {t("of")} {total}
          </Text>
        )}
      </Text>
    </Flex>
    <Flex justifyContent="center" wrap="wrap" gap={2}>
      {/* First Page */}
      <Button
        onClick={() => {
          setCurrentPage(1);
          fetchLogs(1, perPage);
        }}
        disabled={currentPage === 1}
        size="sm"
        variant="outline"
      >
        {t("First")}
      </Button>

      {/* Previous Page */}
      <Button
        onClick={() => {
          const page = Math.max(currentPage - 1, 1);
          setCurrentPage(page);
          fetchLogs(page, perPage);
        }}
        disabled={currentPage === 1}
        size="sm"
        colorScheme="cyan"
        variant="outline"
      >
        {t("Previous")}
      </Button>

      {/* Page Numbers with smart ellipsis */}
      {(() => {
        const delta = 1;
        const range = [];
        const rangeWithDots = [];

        for (let i = Math.max(2, currentPage - delta); i <= Math.min(lastPage - 1, currentPage + delta); i++) {
          range.push(i);
        }

        if (currentPage - delta > 2) {
          rangeWithDots.push(1, '...');
        } else {
          rangeWithDots.push(1);
        }

        rangeWithDots.push(...range);

        if (currentPage + delta < lastPage - 1) {
          rangeWithDots.push('...', lastPage);
        } else if (lastPage > 1) {
          rangeWithDots.push(lastPage);
        }

        return rangeWithDots.map((page, index) => {
          if (page === '...') {
            return (
              <Text key={`ellipsis-${index}`} mx={2} alignSelf="center" fontSize="sm" color="gray.500">
                ...
              </Text>
            );
          }
          return (
            <Button
              key={page}
              onClick={() => {
                setCurrentPage(page);
                fetchLogs(page, perPage);
              }}
              variant={currentPage === page ? "solid" : "outline"}
              colorScheme={currentPage === page ? "cyan" : "gray"}
              size="sm"
              minW="40px"
            >
              {page}
            </Button>
          );
        });
      })()}

      {/* Next Page */}
      <Button
        onClick={() => {
          const page = Math.min(currentPage + 1, lastPage);
          setCurrentPage(page);
          fetchLogs(page, perPage);
        }}
        disabled={currentPage === lastPage}
        size="sm"
        colorScheme="cyan"
        variant="outline"
      >
        {t("Next")}
      </Button>

      {/* Last Page */}
      <Button
        onClick={() => {
          setCurrentPage(lastPage);
          fetchLogs(lastPage, perPage);
        }}
        disabled={currentPage === lastPage}
        size="sm"
        variant="outline"
      >
        {t("Last")}
      </Button>
    </Flex>
  </Box>
)}
      </Box>
    );
  };

  // ---- Mobile cards ----
  const renderMobileView = () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      return (
        <Box p="16px" textAlign="center">
          <Text fontSize="md" color="gray.500">
            {loading ? t("Loading logs...") : t("No logs found for this page.")}
          </Text>
        </Box>
      );
    }

    // Smart pagination window for mobile
    const pageWindowSize = 3;
    let startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    let endPage = Math.min(lastPage, startPage + pageWindowSize - 1);
    if (endPage - startPage < pageWindowSize - 1) {
      startPage = Math.max(1, endPage - pageWindowSize + 1);
    }
    const pages = [];
    for (let p = startPage; p <= endPage; p++) {
      pages.push(p);
    }

    return (
      <Box px="10px">
        {filteredLogs.map((log, index) => {
          const absoluteIndex = (currentPage - 1) * perPage + index + 1;
          const statusColor =
            log.status_code >= 500
              ? "red"
              : log.status_code >= 400
              ? "orange"
              : "green";

          return (
            <Card key={log.id} p="4" mb={3} borderRadius="md" shadow="md">
              <Flex justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.500">
                  #{absoluteIndex}
                </Text>
                <Badge colorScheme={statusColor}>{log.status_code}</Badge>
              </Flex>
              <Text fontSize="sm" fontWeight="bold" mb={1}>
                {log.method} — {log.path}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {log.timestamp}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                IP: {log.ip}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {t("Feature")}: {log.feature_tag}
              </Text>
              <Text fontSize="xs" mt={1}>
                {t("Response time")}: {Number(log.response_time).toFixed(2)} ms
              </Text>
            </Card>
          );
        })}

        {/* Mobile Pagination */}
        {lastPage > 1 && (
          <Flex justify="center" align="center" mt={4} gap={2} wrap="wrap">
            <Button
              size="sm"
              onClick={() => {
                setCurrentPage(1);
                fetchLogs(1, perPage);
              }}
              disabled={currentPage === 1}
              variant="outline"
            >
              {t("First")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const page = Math.max(currentPage - 1, 1);
                setCurrentPage(page);
                fetchLogs(page, perPage);
              }}
              disabled={currentPage === 1}
              colorScheme="cyan"
              variant="outline"
            >
              {t("Previous")}
            </Button>
            {startPage > 1 && (
              <Text mx={2} alignSelf="center" fontSize="sm" color="gray.500">
                ...
              </Text>
            )}
            {pages.map((page) => (
              <Button
                key={page}
                onClick={() => {
                  setCurrentPage(page);
                  fetchLogs(page, perPage);
                }}
                variant={currentPage === page ? "solid" : "outline"}
                colorScheme={currentPage === page ? "cyan" : "gray"}
                size="sm"
                minW="40px"
              >
                {page}
              </Button>
            ))}
            {endPage < lastPage && (
              <Text mx={2} alignSelf="center" fontSize="sm" color="gray.500">
                ...
              </Text>
            )}
            <Button
              size="sm"
              onClick={() => {
                const page = Math.min(currentPage + 1, lastPage);
                setCurrentPage(page);
                fetchLogs(page, perPage);
              }}
              disabled={currentPage === lastPage}
              colorScheme="cyan"
              variant="outline"
            >
              {t("Next")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setCurrentPage(lastPage);
                fetchLogs(lastPage, perPage);
              }}
              disabled={currentPage === lastPage}
              variant="outline"
            >
              {t("Last")}
            </Button>
          </Flex>
        )}
      </Box>
    );
  };

  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ base: "scroll", lg: "hidden" }}
      boxShadow="lg"
      bg="white"
    >
      {/* Header + summary + filters */}
      <Box
        px={{ base: "10px", md: "25px" }}
        py="12px"
        mb="12px"
        bg="gray.100"
        borderBottomWidth="1px"
      >
        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <Box>
            <Text
              color="black"
              fontSize="22px"
              fontWeight="700"
              lineHeight="100%"
            >
              {t(" Logs")}
            </Text>
            {meta && (
              <Flex mt={2} gap={3} flexWrap="wrap">
                <Badge colorScheme="blue" borderRadius="md" px={2} py={1}>
                  {t("Total requests")}: {meta.total_requests}
                </Badge>
                <Badge colorScheme="purple" borderRadius="md" px={2} py={1}>
                  {t("Average response time")}:{" "}
                  {Number(meta.average_response_time).toFixed(2)} ms
                </Badge>
              </Flex>
            )}
          </Box>
          {renderFilterForm()}
        </Flex>
      </Box>

      {isMobile ? renderMobileView() : renderDesktopTable()}
    </Card>
  );
};

export default LogsTable;